'use client';

import { useState, useCallback } from 'react';
import type { BusinessInfo } from '../types/index.js';

interface UseBusinessInfoOptions {
  apiBaseUrl?: string;
}

export function useBusinessInfo(options: UseBusinessInfoOptions = {}) {
  const { apiBaseUrl = '/api/meridian' } = options;
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadBusinessInfo = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/business-info`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        setBusinessInfo(data.data.businessInfo);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load business info');
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl]);

  const updateBusinessInfo = useCallback(
    async (info: BusinessInfo) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${apiBaseUrl}/business-info`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ businessInfo: info }),
          credentials: 'include',
        });
        const data = await response.json();
        if (data.success) {
          setBusinessInfo(info);
        } else {
          setError(data.error);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update business info');
      } finally {
        setIsLoading(false);
      }
    },
    [apiBaseUrl]
  );

  return { businessInfo, isLoading, error, loadBusinessInfo, updateBusinessInfo };
}
